import { Box, Button } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { PersonAdd } from '@mui/icons-material';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useGlobals } from 'src/shared/contexts/appContext';
import FormLoaderWrapper from 'src/shared/components/organisms/formLoaderWrapper';
import { createMemberInvoice } from 'src/shared/services/memberInvoiceService';
import { toast } from 'react-toastify';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { Member } from '../models/member';
import { MemberMeFamilyDataGrid } from './memberMeFamilyDataGrid';
import { MemberMeFamilyAddMemberModal } from './memberMeFamilyAddMemberModal';

type MemberMeFamilyModalProps = {
  modalOpen: boolean;
  onClose: () => void;
  member: Member;
};

export const MemberMeFamilyModal = (props: MemberMeFamilyModalProps) => {
  const { modalOpen, onClose, member } = props;
  const { t } = useTranslation(['member']);
  const { setDateStamp } = useGlobals();
  const isMounted = useIsMounted();

  const [loading, setLoading] = useState<boolean>(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMemberCount, setNewMemberCount] = useState<number>(0);
  const [unpaidNewMembersCount, setUnpaidNewMembersCount] = useState<number>(0);

  const onCloseHandler = () => {
    onClose();
  };

  const onSaveFamilyMemberHandler = () => {
    setShowAddModal(false);
  };

  const mainMemberId = member.familyMembers?.find((mem) => mem.mainMember)?.member_id ?? member.member_id;

  const handleMemberInvoice = async () => {
    setLoading(true);
    try {
      const res = await createMemberInvoice({
        mainMemberId: mainMemberId || 0,
        onlyInterested: true,
        formName: 'member',
      });
      if (isMounted()) {
        if (res.data && res.data.length > 0) {
          window.open(res.data);
        } else {
          toast.success(<>{t('member:allDone')}</>);
        }
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        setLoading(false);
        setDateStamp(new Date());
        setShowAddModal(false);
      }
    }
  };

  return (
    <>
      <GenericDialog
        open={modalOpen}
        onClose={onCloseHandler}
        onSave={() => ConsoleHelper.log('save')}
        dialogTitle={`${t('member:editFamily.header')} (${t('member:member')} #${member.memberNr})`}
        dialogSubTitle={`${member.firstName} ${member.lastName}`}
        padding={0}
        dialogContent={
          <FormLoaderWrapper loading={loading}>
            <MemberMeFamilyDataGrid
              mainMemberId={mainMemberId}
              callbackNewMemberCount={(x) => setNewMemberCount(x)}
              callbackUnpaidNewMembers={(x) => setUnpaidNewMembersCount(x)}
            />
          </FormLoaderWrapper>
        }
        dialogActions={
          <Box display='flex' flexDirection='row' justifyContent='flex-end' width='100%'>
            <Button autoFocus variant='contained' color='secondary' startIcon={<PersonAdd />} disableElevation onClick={() => setShowAddModal(true)}>
              {t('common:button.createFamilyMember')}
            </Button>
            {newMemberCount > 0 && (
              <Button variant='contained' disableElevation onClick={() => handleMemberInvoice()} sx={{ ml: 1 }}>
                {unpaidNewMembersCount ? t('common:button.goToPayment') : t('common:button.complete')}
              </Button>
            )}
          </Box>
        }
      />
      {showAddModal && (
        <MemberMeFamilyAddMemberModal
          modalOpen={showAddModal}
          onClose={() => setShowAddModal(false)}
          member={member}
          onSave={onSaveFamilyMemberHandler}
        />
      )}
    </>
  );
};
